import { observer, useObservable } from "@legendapp/state/react";
import { memo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Brain,
  Shield,
  Check,
  ChevronDown,
  Globe,
  RefreshCw,
  Settings2,
  SlidersHorizontal,
  Sparkles,
  Zap,
} from "lucide-react";
import { AgentSelector } from "@/components/AgentSelector";
import { WorkflowSelector } from "@/components/WorkflowSelector";
import { InfoTooltip } from "@/components/InfoTooltip";
import { RagScopeControl } from "@/components/RagScopeSelector";
import { ScopeRagSummary } from "@/components/chat/ScopeRagSummary";
import { Switch } from "@/components/ui/switch";
import type { OpenRouterModel } from "@/services/aiService";
import type { Workflow } from "@/types/aiToolkit";
import type { Source } from "@shared/schema";

/** Chat mode + context mode (MAX/LITE) are UI toggles; credit billing is enforced in useChatSend before send (same rules for all modes). */

type ModelCategory = "popular" | "general" | "creative" | "fast" | "reasoning" | "code" | "thinking" | "vision";
type CostTier = "low" | "medium" | "high";

interface CuratedModel {
  id: string;
  name: string;
  category: ModelCategory;
  costTier: CostTier;
  supportsThinking?: boolean;
  supportsVision?: boolean;
  description?: string;
}

interface ToolsPopoverProps {
  toolsMenuOpen: boolean;
  setToolsMenuOpen: (open: boolean) => void;
  modelSelectorOpen: boolean;
  setModelSelectorOpen: (open: boolean) => void;
  selectedModel: string;
  setSelectedModel: (modelId: string) => void;
  setModelInfo: (model: OpenRouterModel) => void;
  availableModels: OpenRouterModel[];
  modelsLastUpdated: Date | null;
  refetchModels: () => void;
  isRefreshingModels: boolean;
  isLoadingModels?: boolean;
  curatedModels: CuratedModel[];
  modelsByCategory: Record<ModelCategory, CuratedModel[]>;
  selectedModelInfo: OpenRouterModel | undefined;
  selectedModelSupportsThinking: boolean;
  getCostColor: (tier: CostTier) => string;
  getCostLabel: (tier: CostTier) => string;
  autoSelectBest: boolean;
  setAutoSelectBest: (value: boolean) => void;
  thinkingMode: boolean;
  setThinkingMode: (value: boolean) => void;
  selectedSuperPrompt: any;
  setSelectedSuperPrompt: (value: any) => void;
  selectedAgentId: string | null;
  setSelectedAgentId: (value: string | null) => void;
  selectedWorkflow: Workflow | null;
  setSelectedWorkflow: (value: Workflow | null) => void;
  deepResearchMode: boolean;
  setDeepResearchMode: (value: boolean) => void;
  webSearchMode: "open" | "closed";
  setWebSearchMode: (value: "open" | "closed") => void;
  chatMode: "normal" | "questioning" | "dialogue";
  setChatMode: (value: "normal" | "questioning" | "dialogue") => void;
  contextMode: "MAX" | "LITE";
  setContextMode: (value: "MAX" | "LITE") => void;
  allSelectedSources: Source[];
  showPromptEnhancer: boolean;
  setShowPromptEnhancer: (open: boolean) => void;
  canEnhancePrompt: boolean;
  hasPersonalizationData: boolean;
  usePersonalization: boolean;
  setUsePersonalization: (value: boolean) => void;
  hasSourceOfTruth?: boolean;
  useSourceOfTruth?: boolean;
  setUseSourceOfTruth?: (value: boolean) => void;
  useRAG?: boolean;
  setUseRAG?: (value: boolean) => void;
  /** When chat has a space/topic: control RAG knowledge scope */
  selectedSpaceIds?: string[];
  selectedTopicId?: string | null;
  setSelectedTopicId?: (topicId: string | null) => void;
  spaceTopics?: Array<{ id: string; name: string }>;
  useFullScope?: boolean;
  setUseFullScope?: (value: boolean) => void;
  /** DS EDIT 5 — multi-workspace RAG scope (requires a persisted thread) */
  currentThreadId?: string | null;
  onOpenAdvanced: () => void;
}

export const ToolsPopover = memo(observer(function ToolsPopover({
  toolsMenuOpen,
  setToolsMenuOpen,
  modelSelectorOpen,
  setModelSelectorOpen,
  selectedModel,
  setSelectedModel,
  setModelInfo,
  availableModels,
  modelsLastUpdated,
  refetchModels,
  isRefreshingModels,
  isLoadingModels = false,
  curatedModels,
  modelsByCategory,
  selectedModelInfo,
  selectedModelSupportsThinking,
  getCostColor,
  getCostLabel,
  autoSelectBest,
  setAutoSelectBest,
  thinkingMode,
  setThinkingMode,
  selectedSuperPrompt,
  setSelectedSuperPrompt,
  selectedAgentId,
  setSelectedAgentId,
  selectedWorkflow,
  setSelectedWorkflow,
  deepResearchMode,
  setDeepResearchMode,
  webSearchMode,
  setWebSearchMode,
  chatMode,
  setChatMode,
  contextMode,
  setContextMode,
  allSelectedSources,
  showPromptEnhancer,
  setShowPromptEnhancer,
  canEnhancePrompt,
  hasPersonalizationData,
  usePersonalization,
  setUsePersonalization,
  hasSourceOfTruth = false,
  useSourceOfTruth = true,
  setUseSourceOfTruth,
  useRAG = true,
  setUseRAG,
  selectedSpaceIds = [],
  selectedTopicId = null,
  setSelectedTopicId,
  spaceTopics = [],
  useFullScope = false,
  setUseFullScope,
  currentThreadId = null,
  onOpenAdvanced,
}: ToolsPopoverProps) {
  const hasOpened$ = useObservable(false);
  const isAudioModel = (model: { name?: string; id?: string }) => {
    const label = `${model.name || ""} ${model.id || ""}`.toLowerCase();
    return label.includes("audio");
  };
  const filteredModelsByCategory = Object.fromEntries(
    Object.entries(modelsByCategory).map(([category, models]) => [
      category,
      models.filter((model) => !isAudioModel(model)),
    ])
  ) as typeof modelsByCategory;
  const filteredAvailableModels = availableModels.filter((model) => !isAudioModel(model));

  const selectCuratedModel = (model: CuratedModel) => {
    setSelectedModel(model.id);
    const info = filteredAvailableModels.find((m) => m.id === model.id);
    if (info) setModelInfo(info);
    setModelSelectorOpen(false);
  };

  return (
    <div className="flex items-center gap-1">
      <Popover
        open={toolsMenuOpen}
        onOpenChange={(open) => {
          setToolsMenuOpen(open);
          if (open) hasOpened$.set(true);
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant={toolsMenuOpen ? "default" : "ghost"}
                size="icon"
                className="h-8 w-8 relative"
                data-testid="button-toggle-tools"
                aria-label="Tools and settings"
              >
                <Settings2 className="h-4 w-4" />
                {(webSearchMode === "open" ||
                  deepResearchMode ||
                  chatMode !== "normal" ||
                  thinkingMode ||
                  autoSelectBest) && <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-primary rounded-full" />}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>Tools & Settings</p>
          </TooltipContent>
        </Tooltip>
        {(toolsMenuOpen || hasOpened$.get()) && (
          <PopoverContent
            className="w-80 max-h-[min(70vh,36rem)] overflow-y-auto p-3"
            align="start"
          >
            <div className="space-y-3">
              <div className="text-sm font-medium">Chat Settings</div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Popover open={modelSelectorOpen} onOpenChange={setModelSelectorOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between h-8 text-xs"
                        data-testid="select-model"
                        disabled={autoSelectBest}
                      >
                        <span className="truncate flex items-center gap-1.5">
                          {autoSelectBest ? (
                            <>
                              <Sparkles className="h-3 w-3 text-primary" />
                              Auto-Select Best
                            </>
                          ) : (
                            <>
                              {selectedModelInfo?.name || selectedModel.split("/").pop()}
                              {curatedModels.find((m) => m.id === selectedModel) && (
                                <span
                                  className={`text-[10px] font-medium ${getCostColor(
                                    curatedModels.find((m) => m.id === selectedModel)?.costTier || "medium"
                                  )}`}
                                >
                                  {getCostLabel(
                                    curatedModels.find((m) => m.id === selectedModel)?.costTier || "medium"
                                  )}
                                </span>
                              )}
                            </>
                          )}
                        </span>
                        <ChevronDown className="h-3 w-3 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[380px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search models..." />
                        <CommandList className="max-h-[350px]">
                          {isLoadingModels && filteredAvailableModels.length === 0 ? (
                            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                              Loading models…
                            </div>
                          ) : (
                            <CommandEmpty>No models found.</CommandEmpty>
                          )}

                          {filteredModelsByCategory.popular.length > 0 && (
                            <CommandGroup heading="Popular (Latest)">
                              {filteredModelsByCategory.popular.map((model) => (
                                <CommandItem
                                  key={model.id}
                                  value={`${model.id} ${model.name}`}
                                  onSelect={() => selectCuratedModel(model)}
                                  className="flex items-center justify-between"
                                >
                                  <div className="flex items-center gap-2">
                                    <Check className={`h-3 w-3 ${selectedModel === model.id ? "opacity-100" : "opacity-0"}`} />
                                    <span>{model.name}</span>
                                  </div>
                                  <span className={`text-[10px] font-bold ${getCostColor(model.costTier)}`}>
                                    {getCostLabel(model.costTier)}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}

                          {filteredModelsByCategory.fast.length > 0 && (
                            <CommandGroup heading="Fast & Cheap">
                              {filteredModelsByCategory.fast.map((model) => (
                                <CommandItem
                                  key={model.id}
                                  value={`${model.id} ${model.name}`}
                                  onSelect={() => selectCuratedModel(model)}
                                  className="flex items-center justify-between"
                                >
                                  <div className="flex items-center gap-2">
                                    <Check className={`h-3 w-3 ${selectedModel === model.id ? "opacity-100" : "opacity-0"}`} />
                                    <span>{model.name}</span>
                                    <span className="text-[10px] text-muted-foreground">{model.description}</span>
                                  </div>
                                  <span className={`text-[10px] font-bold ${getCostColor(model.costTier)}`}>
                                    {getCostLabel(model.costTier)}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}

                          {filteredModelsByCategory.thinking.length > 0 && (
                            <CommandGroup heading="Thinking (Reasoning)">
                              {filteredModelsByCategory.thinking.map((model) => (
                                <CommandItem
                                  key={model.id}
                                  value={`${model.id} ${model.name}`}
                                  onSelect={() => selectCuratedModel(model)}
                                  className="flex items-center justify-between"
                                >
                                  <div className="flex items-center gap-2">
                                    <Check className={`h-3 w-3 ${selectedModel === model.id ? "opacity-100" : "opacity-0"}`} />
                                    <span>{model.name}</span>
                                    <Badge variant="outline" className="h-4 text-[9px] px-1">
                                      Think
                                    </Badge>
                                  </div>
                                  <span className={`text-[10px] font-bold ${getCostColor(model.costTier)}`}>
                                    {getCostLabel(model.costTier)}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}

                          {filteredModelsByCategory.general.length > 0 && (
                            <CommandGroup heading="General Purpose">
                              {filteredModelsByCategory.general.map((model) => (
                                <CommandItem
                                  key={model.id}
                                  value={`${model.id} ${model.name}`}
                                  onSelect={() => selectCuratedModel(model)}
                                  className="flex items-center justify-between"
                                >
                                  <div className="flex items-center gap-2">
                                    <Check className={`h-3 w-3 ${selectedModel === model.id ? "opacity-100" : "opacity-0"}`} />
                                    <span>{model.name}</span>
                                  </div>
                                  <span className={`text-[10px] font-bold ${getCostColor(model.costTier)}`}>
                                    {getCostLabel(model.costTier)}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}

                          {filteredModelsByCategory.creative.length > 0 && (
                            <CommandGroup heading="Creative Writing">
                              {filteredModelsByCategory.creative.map((model) => (
                                <CommandItem
                                  key={model.id}
                                  value={`${model.id} ${model.name}`}
                                  onSelect={() => selectCuratedModel(model)}
                                  className="flex items-center justify-between"
                                >
                                  <div className="flex items-center gap-2">
                                    <Check className={`h-3 w-3 ${selectedModel === model.id ? "opacity-100" : "opacity-0"}`} />
                                    <span>{model.name}</span>
                                  </div>
                                  <span className={`text-[10px] font-bold ${getCostColor(model.costTier)}`}>
                                    {getCostLabel(model.costTier)}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}

                          {filteredModelsByCategory.reasoning.length > 0 && (
                            <CommandGroup heading="Math & Reasoning">
                              {filteredModelsByCategory.reasoning.map((model) => (
                                <CommandItem
                                  key={model.id}
                                  value={`${model.id} ${model.name}`}
                                  onSelect={() => selectCuratedModel(model)}
                                  className="flex items-center justify-between"
                                >
                                  <div className="flex items-center gap-2">
                                    <Check className={`h-3 w-3 ${selectedModel === model.id ? "opacity-100" : "opacity-0"}`} />
                                    <span>{model.name}</span>
                                  </div>
                                  <span className={`text-[10px] font-bold ${getCostColor(model.costTier)}`}>
                                    {getCostLabel(model.costTier)}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}

                          {filteredModelsByCategory.code.length > 0 && (
                            <CommandGroup heading="Code">
                              {filteredModelsByCategory.code.map((model) => (
                                <CommandItem
                                  key={model.id}
                                  value={`${model.id} ${model.name}`}
                                  onSelect={() => selectCuratedModel(model)}
                                  className="flex items-center justify-between"
                                >
                                  <div className="flex items-center gap-2">
                                    <Check className={`h-3 w-3 ${selectedModel === model.id ? "opacity-100" : "opacity-0"}`} />
                                    <span>{model.name}</span>
                                  </div>
                                  <span className={`text-[10px] font-bold ${getCostColor(model.costTier)}`}>
                                    {getCostLabel(model.costTier)}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}

                          {filteredModelsByCategory.vision.length > 0 && (
                            <CommandGroup heading="Vision (Image Analysis)">
                              {filteredModelsByCategory.vision.map((model) => (
                                <CommandItem
                                  key={`vision-${model.id}`}
                                  value={`vision ${model.id} ${model.name}`}
                                  onSelect={() => selectCuratedModel(model)}
                                  className="flex items-center justify-between"
                                >
                                  <div className="flex items-center gap-2">
                                    <Check className={`h-3 w-3 ${selectedModel === model.id ? "opacity-100" : "opacity-0"}`} />
                                    <span>{model.name}</span>
                                    <Badge variant="outline" className="h-4 text-[9px] px-1">
                                      Vision
                                    </Badge>
                                  </div>
                                  <span className={`text-[10px] font-bold ${getCostColor(model.costTier)}`}>
                                    {getCostLabel(model.costTier)}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}

                          <CommandGroup heading={`All Models (${filteredAvailableModels.length})`}>
                            {filteredAvailableModels.map((model) => (
                              <CommandItem
                                key={model.id}
                                value={`${model.id} ${model.name || ""}`}
                                onSelect={() => {
                                  setModelInfo(model);
                                  setSelectedModel(model.id);
                                  setModelSelectorOpen(false);
                                }}
                              >
                                <Check className={`mr-2 h-3 w-3 ${selectedModel === model.id ? "opacity-100" : "opacity-0"}`} />
                                <span className="truncate">{model.name || model.id.split("/").pop()}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>

                          <div className="border-t p-2">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[10px] text-muted-foreground">
                                {modelsLastUpdated ? `Updated: ${modelsLastUpdated.toLocaleDateString()}` : "Never refreshed"}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs"
                                onClick={() => refetchModels()}
                                disabled={isRefreshingModels}
                                data-testid="button-refresh-models"
                              >
                                <RefreshCw className={`h-3 w-3 mr-1 ${isRefreshingModels ? "animate-spin" : ""}`} />
                                {isRefreshingModels ? "Refreshing..." : "Check for new models"}
                              </Button>
                            </div>
                          </div>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button
                    variant={autoSelectBest ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setAutoSelectBest(!autoSelectBest)}
                    className="h-8 px-2 text-xs flex-shrink-0"
                    data-testid="button-auto-select"
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    Auto
                  </Button>
                </div>

                {selectedModelSupportsThinking && (
                  <div className="flex items-center justify-between gap-2 min-h-8 pt-1">
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Brain className="h-3.5 w-3.5" />
                      Thinking Mode
                    </span>
                    <Switch
                      checked={thinkingMode}
                      onCheckedChange={setThinkingMode}
                      aria-label="Thinking mode"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 min-h-8">
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Globe className="h-3.5 w-3.5" />
                    Web Search
                  </span>
                  <Switch
                    checked={webSearchMode === "open"}
                    onCheckedChange={(v) => setWebSearchMode(v ? "open" : "closed")}
                    data-testid="button-web-search"
                    aria-label="Web search"
                  />
                </div>

                <div className="flex items-center justify-between gap-2 min-h-8">
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Zap className="h-3.5 w-3.5" />
                    Deep Research
                  </span>
                  <Switch
                    checked={deepResearchMode}
                    onCheckedChange={setDeepResearchMode}
                    data-testid="button-deep-research"
                    aria-label="Deep research mode"
                  />
                </div>

                <div className="flex items-center justify-between gap-2 min-h-8">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    Chat Mode
                    <InfoTooltip content="Question mode asks clarifying questions; Dialogue keeps a conversational back-and-forth." />
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant={chatMode === "normal" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setChatMode("normal")}
                      className="h-8 px-2 text-xs"
                    >
                      Normal
                    </Button>
                    <Button
                      variant={chatMode === "questioning" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setChatMode("questioning")}
                      className="h-8 px-2 text-xs"
                    >
                      Q
                    </Button>
                    <Button
                      variant={chatMode === "dialogue" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setChatMode("dialogue")}
                      className="h-8 px-2 text-xs"
                    >
                      D
                    </Button>
                  </div>
                </div>

                {allSelectedSources.length > 0 && (
                  <div className="flex items-center justify-between gap-2 min-h-8">
                    <span className="text-xs text-muted-foreground">Context Mode</span>
                    <Button
                      variant={contextMode === "MAX" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setContextMode(contextMode === "MAX" ? "LITE" : "MAX")}
                      className="h-8 text-xs"
                    >
                      {contextMode}
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center justify-between gap-2 min-h-8">
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Brain className="h-3.5 w-3.5" />
                    Personalization
                    <InfoTooltip
                      content={
                        hasPersonalizationData
                          ? "Use your personality, worldview & goals in responses."
                          : "Add profile data in Settings to enable personalization."
                      }
                    />
                  </span>
                  <Switch
                    checked={usePersonalization}
                    onCheckedChange={setUsePersonalization}
                    disabled={!hasPersonalizationData}
                    data-testid="button-toggle-personalization"
                    aria-label="Use personalization"
                  />
                </div>

                {hasSourceOfTruth && setUseSourceOfTruth && (
                  <div className="flex items-center justify-between gap-2 min-h-8">
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Shield className="h-3.5 w-3.5" />
                      Source of Truth
                      <InfoTooltip content="Always include your canon documents in context." />
                    </span>
                    <Switch
                      checked={useSourceOfTruth}
                      onCheckedChange={setUseSourceOfTruth}
                      data-testid="button-toggle-source-of-truth"
                      aria-label="Use Source of Truth documents"
                    />
                  </div>
                )}

                {setUseRAG && (
                  <div className="flex items-center justify-between gap-2 min-h-8">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      Semantic search (RAG)
                      <InfoTooltip content="Search your embedded documents by meaning." />
                    </span>
                    <Switch checked={useRAG} onCheckedChange={setUseRAG} aria-label="Semantic search (RAG)" />
                  </div>
                )}

                <Accordion type="multiple" className="space-y-2 pt-2 border-t">
                  {selectedSpaceIds.length > 0 && setSelectedTopicId && (
                    <AccordionItem
                      value="space-topic"
                      className="border rounded-lg px-2 border-b"
                      data-testid="accordion-space-topic"
                    >
                      <AccordionTrigger className="py-2 text-xs font-medium hover:no-underline hover:bg-muted/50 rounded-md px-1">
                        <span className="flex flex-col items-start gap-0.5 text-left min-w-0">
                          <span>Space / topic filter</span>
                          <span className="text-[10px] font-normal text-muted-foreground truncate max-w-[14rem]">
                            {selectedTopicId == null
                              ? "Entire space"
                              : spaceTopics.find((t) => t.id === selectedTopicId)?.name ??
                                "Selected topic"}
                          </span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="pb-2 px-1">
                        <p className="text-xs text-muted-foreground mb-1.5">
                          Limit AI retrieval to this space or a topic
                        </p>
                        <div className="max-h-56 overflow-y-auto space-y-0.5 pr-0.5">
                          <div className="flex items-center justify-between gap-2 min-h-8 px-0.5">
                            <span className="text-xs text-muted-foreground truncate">
                              Entire space
                            </span>
                            <Switch
                              checked={selectedTopicId == null}
                              onCheckedChange={(checked) => {
                                if (checked) setSelectedTopicId(null);
                              }}
                              data-testid="button-rag-scope-entire-space"
                              aria-label="Entire space"
                            />
                          </div>
                          {spaceTopics.map((topic) => {
                            const isOn = selectedTopicId === topic.id;
                            return (
                              <div
                                key={topic.id}
                                className="flex items-center justify-between gap-2 min-h-8 px-0.5"
                              >
                                <span
                                  className="text-xs text-muted-foreground truncate min-w-0"
                                  title={topic.name}
                                >
                                  {topic.name}
                                </span>
                                <Switch
                                  checked={isOn}
                                  onCheckedChange={(checked) => {
                                    setSelectedTopicId(checked ? topic.id : null);
                                  }}
                                  data-testid={`button-rag-scope-topic-${topic.id}`}
                                  aria-label={topic.name}
                                />
                              </div>
                            );
                          })}
                        </div>
                        {selectedSpaceIds[0] && selectedTopicId == null && (
                          <ScopeRagSummary scopeType="space" scopeId={selectedSpaceIds[0]} />
                        )}
                        {selectedTopicId && (
                          <ScopeRagSummary scopeType="topic" scopeId={selectedTopicId} />
                        )}
                        {setUseFullScope && (selectedSpaceIds.length > 0 || selectedTopicId) && (
                          <div className="flex items-center justify-between gap-2 min-h-8 px-0.5 pt-1">
                            <span className="text-xs text-muted-foreground">Use full scope in context</span>
                            <Switch
                              checked={useFullScope}
                              onCheckedChange={setUseFullScope}
                              data-testid="button-rag-full-scope"
                              aria-label="Use full scope in context"
                            />
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  )}

                  <AccordionItem
                    value="workspaces"
                    className="border rounded-lg px-2 border-b"
                    data-testid="accordion-workspaces"
                  >
                    <AccordionTrigger className="py-2 text-xs font-medium hover:no-underline hover:bg-muted/50 rounded-md px-1">
                      Workspaces
                    </AccordionTrigger>
                    <AccordionContent className="pb-2 px-1">
                      <RagScopeControl
                        threadId={currentThreadId}
                        variant="panel"
                        hideHeading
                      />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <div className="space-y-1.5">
                  <AgentSelector
                    value={selectedAgentId}
                    onValueChange={setSelectedAgentId}
                    isActive={toolsMenuOpen}
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-muted-foreground">Workflow</label>
                    <InfoTooltip content="Multi-step automations that chain agents and prompts together." />
                  </div>
                  <WorkflowSelector selectedWorkflow={selectedWorkflow} onWorkflowSelect={setSelectedWorkflow} />
                </div>
              </div>

              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full h-8 text-xs justify-start gap-1.5 text-muted-foreground"
                  onClick={() => {
                    setToolsMenuOpen(false);
                    onOpenAdvanced();
                  }}
                  data-testid="button-open-advanced-settings"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  Advanced Settings...
                </Button>
              </div>
            </div>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
}));
